import { useApiAction } from "api/drops/useApiAction";
import { userListTelephoneApi } from "api/puzzel/puzzel";
import { useFeatureFlag } from "app/FeatureFlags/useFeatureFlags";
import { PUZZEL_OIDC_LOGIN_FLAG } from "app/PuzzelAuth/puzzelAuthConfig";
import {
  getPuzzelUserId,
  getPuzzelUsername,
} from "app/PuzzelAuth/puzzelTokenStorage";
import { usePuzzelAuthenticationContext } from "features/Header/PuzzelTelephone/provider/contexts/PuzzelAuthenticationContext";
import { UserDetailsAndLogOnOffProvider } from "features/Header/PuzzelTelephone/provider/contexts/UserDetailsAndLogOnOffContext";
import { useSetUserValidation } from "features/Header/PuzzelTelephone/provider/contexts/UserValidationContext";
import { CallProvider } from "features/Header/PuzzelTelephone/provider/providers/CallStatusProvider";
import { ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SkeletonButton } from "shared/components/feedback/SkeletonLoader/SkeletonLoaderStyles";
import { Button, Flex, useColorMode } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { useUserInfo } from "shared/hooks/useUserInfo";

type Props = {
  children: ReactNode;
};
const ValidateUserAndPresentLogon = (props: Props) => {
  const setUserId = useSetUserValidation();
  const { isAuthenticated, login, loginFailed } =
    usePuzzelAuthenticationContext();
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  // Spor's surface.brand token: flips between a light tint (dark mode) and the brand teal (light mode)
  const loginButtonBackground = colorMode === "dark" ? "#B2DFD7" : "#00685E";
  // Spor's text.brand token: dark text on the light tint (dark mode), white on the brand teal (light mode)
  const loginButtonTextColor = colorMode === "dark" ? "#00453E" : "#FFFFFF";
  const { enabled: oidcEnabled, isLoading: flagLoading } = useFeatureFlag(
    PUZZEL_OIDC_LOGIN_FLAG,
  );

  // Legacy: matches the Puzzel user list against the logged-in Vy username.
  // TODO: remove this lookup (and the puzzelOidcLogin flag/branch) once OIDC has rolled out
  // the JWT claim lookup below is both simpler and not reliant on username matching.
  const { username } = useUserInfo();
  const [userListRequest, performGetUserList] = useApiAction(
    userListTelephoneApi,
    false,
  );

  useEffect(() => {
    if (!oidcEnabled && isAuthenticated) {
      performGetUserList();
    }
  }, [oidcEnabled, isAuthenticated]);

  const legacyUserId = userListRequest.data?.result?.find(
    (it: any) => it.userName === username,
  )?.id;

  // OIDC: agent user id/username come straight from the access token - no more fragile
  // username matching.
  const oidcUserId = oidcEnabled && isAuthenticated ? getPuzzelUserId() : null;

  const puzzelUserId = oidcEnabled ? oidcUserId : legacyUserId;

  useEffect(() => {
    if (puzzelUserId) {
      setUserId({ userId: puzzelUserId });
      if (!oidcEnabled) {
        localStorage.setItem("puzzelUserId", puzzelUserId);
      }
    } else {
      setUserId({ userId: null });
      if (!oidcEnabled) {
        localStorage.removeItem("puzzelUserId");
      }
    }
  }, [puzzelUserId, oidcEnabled]);

  if (flagLoading) {
    return <SkeletonButton />;
  }

  if (oidcEnabled && !isAuthenticated) {
    return (
      <Flex gap="3" alignItems="center">
        {loginFailed && (
          <Text>
            {t(
              "Innlogging i Puzzel feilet. Prøv igjen, eller kontakt IT-support.",
            )}
          </Text>
        )}
        <Button
          size="sm"
          variant="secondary"
          onClick={login}
          backgroundColor={loginButtonBackground}
          color={loginButtonTextColor}
        >
          {t("Logg inn i Puzzel")}
        </Button>
      </Flex>
    );
  }

  if (!puzzelUserId) {
    return (
      <Text
        style={{
          marginRight: "2.5rem",

          // Behaviour: Use max 3 lines and show "..." at overflow
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: "3",
          WebkitBoxOrient: "vertical",
        }}
      >
        {t(
          "Bruker ({{username}}) er ikke registrert i Puzzel. Ta kontakt med admin",
          {
            username: oidcEnabled
              ? (getPuzzelUsername() ?? t("Ukjent navn"))
              : username,
          },
        )}
      </Text>
    );
  }

  const { children } = props;
  return (
    <UserDetailsAndLogOnOffProvider>
      <CallProvider>{children}</CallProvider>
    </UserDetailsAndLogOnOffProvider>
  );
};

export default ValidateUserAndPresentLogon;
